<template>
  <ItemTable
    ref="itemTable"
    bulkActionText="Resend Command"
    :columns="columns"
    :details="details"
    :disableBoundCount="disableBoundCount"
    :disableBulkAction="disableBulkAction"
    :emptyCollectionMessage="emptyCollectionMessage"
    :handleBulkAction="handleBulkAction"
    :hasSelectColumn="hasSelectColumn"
    :inDetailsAsidePanel="inDetailsAsidePanel"
    :inPanel="inPanel"
    :itemType="itemType"
    :legacyRowStyles="legacyRowStyles"
    :showActionBar="showActionBar"
    :showBulkAction="isAppleSoftware"
    :showSearchForm="showSearchForm"
    :useLargeTableCells="useLargeTableCells"
  />
</template>

<script>
import {
  ItemTableConsts, ItemTypeConsts, ItemTypeDisplayNames, StoreModuleMap,
} from 'client/util/Constants';
import { mapActions, mapGetters } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import ItemTable from 'client/components/ItemTable/ItemTable.vue';
import ItemTableComputedMixin from 'client/components/ItemTable/mixins/ItemTableComputedMixin';
import SoftwareAppsStatusLabel from 'client/components/ItemTable/BodyComponents/SoftwareAppsStatusLabel.vue';
import SoftwareAppsStatusTimestamp from 'client/components/ItemTable/BodyComponents/SoftwareAppsStatusTimestamp.vue';
import ViewSoftwareAppStatusButton from 'client/components/ItemTable/BodyComponents/ViewSoftwareAppStatusButton.vue';

const softwareAppsStatusCollection = StoreModuleMap[ItemTypeConsts.softwareAppsStatus];
const { columnTypes } = ItemTableConsts;

export default {
  name: 'SoftwareAppsStatusItemTable',

  components: {
    ItemTable,
  },

  mixins: [
    ItemTableComputedMixin,
  ],

  props: {
    isAppleCustomApp: {
      type: Boolean,
      default: false,
    },
    isAppleVpp: {
      type: Boolean,
      default: false,
    },
    softwareAppsId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      itemType: ItemTypeConsts.softwareAppsStatus,
      legacyRowStyles: false,
      details: false,
      disableBoundCount: true,
      disableBulkAction: false,
      emptyCollectionMessage: 'No app status results available',
      hasSelectColumn: true,
      inPanel: false,
      showActionBar: true,
      showBulkAction: !!this.isAppleVpp,
      showSearchForm: false,
      useLargeTableCells: false,
      inDetailsAsidePanel: true,
      columns: [
        {
          bodyComponent: 'ItemName',
          bodyComponentProps: {
            titleField: `${ItemTypeConsts.device}.displayName`,
            indirectItem: `${ItemTypeConsts.device}`,
            itemType: `${ItemTypeConsts.device}`,
          },
          classNames: {
            body: [this.$style.nameColumn, columnTypes.nameColumn],
            heading: [this.$style.nameColumn],
          },
          headingText: `${ItemTypeDisplayNames[ItemTypeConsts.device]} Name`,
        },
        {
          bodyComponent: SoftwareAppsStatusLabel,
          bodyComponentProps: {
            isAppleVpp: this.isAppleVpp,
          },
          headingText: 'Status',
        },
        {
          dataField: 'version',
          headingText: 'Version',
        },
        {
          headingText: 'Details',
          bodyComponent: ViewSoftwareAppStatusButton,
          classNames: {
            body: [this.$style.detailsColumn],
            heading: [this.$style.detailsColumn],
          },
        },
      ],
    };
  },

  computed: {
    isAppleSoftware() {
      return this.isAppleVpp || this.isAppleCustomApp;
    },
  },

  created() {
    if (this.isAppleSoftware) {
      this.columns = this.columns.filter(col => col.headingText !== 'Version');
      this.columns.splice(2, 0, {
        bodyComponent: SoftwareAppsStatusTimestamp,
        headingText: 'Command Timestamp',
        classNames: {
          body: [this.$style.commandNameColumn],
          heading: [this.$style.commandNameColumn],
        },
      });
    }
  },

  mounted() {
    this.setSoftwareAppsId({ softwareAppsId: this.softwareAppsId });
    this.setIsAppleVpp({ isAppleVpp: this.isAppleVpp });
  },

  methods: {
    ...mapActions(notificationModule, [
      'triggerErrorNotification',
      'triggerSuccessNotification',
    ]),

    ...mapGetters(softwareAppsStatusCollection, [
      'getBoundSystemsEligibleForRetry',
    ]),

    ...mapActions(softwareAppsStatusCollection, [
      'setIsAppleVpp',
      'setSoftwareAppsId',
      'bulkRetryInstall',
      'unselectAllItems',
    ]),

    async handleBulkAction() {
      const boundSystems = this.getBoundSystemsEligibleForRetry();
      try {
        await this.bulkRetryInstall();
        this.triggerSuccessNotification({
          message: `Command successfully sent to ${boundSystems} device(s).`,
          showCloseButton: true,
        });
      } catch {
        this.triggerErrorNotification({
          message: 'An error occurred and Bulk Resend for commands could not be sent.',
          showCloseButton: true,
        });
      }
      this.disableBulkAction = true;
      this.unselectAllItems();
    },
  },
};
</script>

<style module>

.commandNameColumn.commandNameColumn {
  white-space: pre-wrap;
}

.nameColumn {
  padding-left: 20px !important;
}

@media (max-width: 1500px) {
  .detailsColumn.detailsColumn {
    padding-right: 20px;
  }
}

</style>
